import styled from "styled-components";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Styles from "@themes/Styles";
import { darkTheme, lightTheme } from "@themes/Theme";

const DropdownMenuTrigger = styled(DropdownMenu.Trigger)`
    width: 200px;
    height: 30px;
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    background: ${({ theme }) => theme.backgroundPrimary};
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${({ theme }) => theme.textColorPrimary};
    cursor: pointer;
`;

const DropdownMenuContent = styled(DropdownMenu.Content)`
    background: ${({ theme }) => theme.backgroundPrimary};
    width: 200px;
    box-shadow: ${({ theme }) => `${theme.boxShadow} ${theme.boxShadowColor}`};
    border-radius: ${Styles.borderRadiusSharp};
`;

const DropdownMenuRadioItem = styled(DropdownMenu.RadioItem)`
    font-size: 13px;
    padding: 4px 5px 4px 16px;
    user-select: none;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.hoverPrimary};
    }
`;

const DropdownMenuItemIndicator = styled(DropdownMenu.ItemIndicator)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    position: absolute;
    left: 0;
    width: 16px;
    height: 16px;
`;

const Placeholder = styled.span`
    min-width: 0;
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
`;

const StyledCaretIcon = styled(CaretIconToggle)`
    flex-shrink: 0;
`;

type LifeAtSingleSelectDropdownProps = {
    options: { label: string; value: string }[];
    selected: string | null;
    onChange: (value: string | null) => void;
    className?: string;
};

export function LifeAtSingleSelectDropdown(props: LifeAtSingleSelectDropdownProps) {
    const placeholder = (() => {
        for (const { label, value } of props.options) {
            if (value === props.selected) {
                return label;
            }
        }
        return "Select...";
    })();

    return (
        <DropdownMenu.Root>
            <DropdownMenuTrigger className={props.className}>
                <Placeholder>{placeholder}</Placeholder>
                <StyledCaretIcon showOpen={false} />
            </DropdownMenuTrigger>
            <DropdownMenu.Portal>
                <DropdownMenuContent sideOffset={4}>
                    <DropdownMenu.RadioGroup
                        value={props.selected ?? ""}
                        onValueChange={(value) => props.onChange(value)}
                    >
                        {props.options.map(({ label, value }) => (
                            <DropdownMenuRadioItem key={value} value={value}>
                                <DropdownMenuItemIndicator>
                                    <CheckMark />
                                </DropdownMenuItemIndicator>
                                <span>{label}</span>
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenu.RadioGroup>
                </DropdownMenuContent>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

const CheckMark = () => {
    const { lightMode } = useAppearanceContext();
    const theme = lightMode ? lightTheme : darkTheme;

    return (
        <svg
            width="12px"
            height="12px"
            fill="none"
            stroke={theme.textColorPrimary}
            strokeWidth="2px"
            viewBox="4 4 16 18"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
};
